import  ApiHelper  from '../../ApiHelper';
import  API_ENDPOINTS  from '../../endpoints';

/**
 * User registration API requests
 */
class RegistrationHelper {
    /**
     * Register a new user
     * @param {Object} userData - User registration data
     * @returns {Promise<Object>} Registration response
     */
    async registerUser(userData) {
        try {
            const registrationData = this.validateRegistrationData(userData);
            
            const response = await ApiHelper.post(
                API_ENDPOINTS.AUTH.REGISTER, 
                registrationData
            );

            return response;
        } catch (error) {
            throw new Error(`Registration failed: ${error.message}`);
        }
    }

    /**
     * Register student with college enrollment
     * @param {Object} studentData - Student registration data
     * @returns {Promise<Object>} Registration response
     */
    async registerStudent(studentData) {
        try {
            const studentRegistrationData = {
                ...this.validateRegistrationData(studentData),
                role: 'student',
                college_id: studentData.college_id,
                // Additional student-specific fields
                registration_no: studentData.registration_no,
                course_enrollments: studentData.courses || []
            };

            return await ApiHelper.post(
                API_ENDPOINTS.AUTH.REGISTER,
                studentRegistrationData
            );
        } catch (error) {
            throw new Error(`Student registration failed: ${error.message}`);
        }
    }

    /**
     * Register SME (Subject Matter Expert)
     * @param {Object} smeData - SME registration data
     * @returns {Promise<Object>} Registration response
     */
    async registerSME(smeData) {
        try {
            const smeRegistrationData = {
                ...this.validateRegistrationData(smeData),
                role: 'sme',
                specialization: smeData.specialization,
                qualification: smeData.qualification,
                experience: smeData.experience
            };

            return await ApiHelper.post(
                API_ENDPOINTS.AUTH.REGISTER,
                smeRegistrationData
            );
        } catch (error) {
            throw new Error(`SME registration failed: ${error.message}`);
        }
    }

    /**
     * Register college administrator
     * @param {Object} collegeData - College admin registration data
     * @returns {Promise<Object>} Registration response
     */
    async registerCollegeAdmin(collegeData) {
        try {
            const collegeRegistrationData = {
                ...this.validateRegistrationData(collegeData),
                role: 'college',
                college_id: collegeData.college_id,
                permissions: collegeData.permissions || [
                    'student_management',
                    'course_management',
                    'progress_tracking'
                ]
            };

            return await ApiHelper.post(
                API_ENDPOINTS.AUTH.REGISTER,
                collegeRegistrationData
            );
        } catch (error) {
            throw new Error(`College admin registration failed: ${error.message}`);
        }
    }

    /**
     * Bulk register users from CSV/Excel data
     * @param {Array} usersArray - Array of user data objects
     * @param {string} role - User role for all users
     * @returns {Promise<Object>} Bulk registration response
     */
    async bulkRegisterUsers(usersArray, role = 'student') {
        try {
            const bulkData = {
                users: usersArray.map(user => ({
                    ...this.validateRegistrationData(user),
                    role
                })),
                send_credentials: true // Send login credentials via email
            };

            return await ApiHelper.post('/admin/bulk-create-users', bulkData);
        } catch (error) {
            throw new Error(`Bulk registration failed: ${error.message}`);
        }
    }

    /**
     * Validate registration data
     * @param {Object} userData - User data to validate
     * @returns {Object} Validated and formatted data
     * @private
     */
    validateRegistrationData(userData) {
        const required = ['email', 'password', 'first_name', 'last_name'];
        const missing = required.filter(field => !userData[field]);
        
        if (missing.length > 0) {
            throw new Error(`Missing required fields: ${missing.join(', ')}`);
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userData.email)) {
            throw new Error('Invalid email format');
        }

        // Password strength validation
        if (userData.password.length < 8) {
            throw new Error('Password must be at least 8 characters long');
        }

        return {
            email: userData.email.toLowerCase().trim(),
            password: userData.password,
            first_name: userData.first_name.trim(),
            last_name: userData.last_name.trim(),
            phone: userData.phone?.trim() || null,
            college_id: userData.college_id || null
        };
    }

    /**
     * Check if email is already registered
     * @param {string} email - Email to check
     * @returns {Promise<boolean>} Whether email exists
     */
    async checkEmailExists(email) {
        try {
            const response = await ApiHelper.get('/auth/check-email', {
                email: email.toLowerCase().trim()
            });
            return response.data.exists;
        } catch (error) {
            console.error('Email check failed:', error);
            return false; // Assume email doesn't exist on error
        }
    }
}

export default new RegistrationHelper();
